import React, { useEffect, useState, useRef, useMemo } from 'react';
import './CSS/Home.css';
import ProductCarousel from '../Components/Slider/Slider';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { blockTextStyle, buildDefaultSections, resolveSections } from '../utils/homeLayout';
import BannerBlock from '../Components/HomeBlocks/BannerBlock';
import RichTextBlock from '../Components/HomeBlocks/RichTextBlock';
import SpacerBlock from '../Components/HomeBlocks/SpacerBlock';

function Home({ setPageReady }) {
  const [categories, setCategories] = useState([]);
  const [dashboardSettings, setDashboardSettings] = useState(null);
  const [sections, setSections] = useState(null);
  const [pickedProducts, setPickedProducts] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [structureReady, setStructureReady] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [myProducts, setMyProducts] = useState([]);
  const showText = structureReady;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const swiperRef = useRef(null);
  const videoRefs = useRef([]);
  const videoTimes = useRef([]);

  useEffect(() => {
    setStructureReady(true);
  }, []);

  useEffect(() => {
    if (!structureReady) return;

    const fetchSettings = async () => {
      try {
        const res = await fetch(`${backendUrl}/dashboard-settings`);
        const data = await res.json();
        if (data.success && data.settings) {
          if (data.settings.mainMedia?.length) {
            setMediaItems(data.settings.mainMedia);
          }
          setDashboardSettings(data.settings);
          if (Array.isArray(data.settings.sections) && data.settings.sections.length) {
            setSections(data.settings.sections);
          }
        }
      } catch (err) {
        console.error('Failed to load dashboard settings', err);
      } finally {
        setPageReady(true);
        setMediaReady(true);
      }
    };

    fetchSettings();
  }, [structureReady, backendUrl, setPageReady]);

  useEffect(() => {
    if (mediaReady && swiperRef.current?.swiper) {
      const swiper = swiperRef.current.swiper;
      swiper.update();
      swiper.slideToLoop(0, 0);
    }
  }, [mediaReady]);

  useEffect(() => {
    const fetchCategoriesWithProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/category/categories-with-products`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        const result = await response.json();
        if (result.success) {
          // Order comes from the saved layout now, so keep the API order here.
          setCategories(result.categories);
        }
      } catch (error) {
        console.error('Error fetching categories with products:', error);
      }
    };
    fetchCategoriesWithProducts();
  }, [backendUrl]);

  // Hand-picked rows reference products by id, which may sit outside the
  // categories the page already loaded, so fetch the catalogue - but only when
  // the layout actually contains such a block.
  useEffect(() => {
    if (!sections?.some((section) => section.type === 'productRow')) return;

    const fetchAllProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/product/get`);
        const result = await response.json();
        if (result.success) {
          setPickedProducts(result.products.filter((p) => p.status !== false));
        }
      } catch (error) {
        console.error('Error fetching products for picked rows:', error);
      }
    };

    fetchAllProducts();
  }, [sections, backendUrl]);

  useEffect(() => {
    const fetchMyProducts = async () => {
      const token = localStorage.getItem('auth-token');
      if (!token) {
        setMyProducts([]);
        return;
      }

      try {
        const response = await fetch(`${backendUrl}/permissions/my-products`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token,
          },
        });
        const result = await response.json();
        if (result.success) {
          setMyProducts(result.products);
        }
      } catch (error) {
        console.error('Error fetching my products:', error);
      }
    };

    fetchMyProducts();

    const handleAuthChange = () => fetchMyProducts();
    window.addEventListener('auth-change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      window.removeEventListener('auth-change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [backendUrl]);

  const playVideoOnSlideChange = (swiper) => {
    // Use realIndex for loop mode
    const activeIndex = swiper?.realIndex ?? swiper?.activeIndex;
    if (typeof activeIndex !== 'undefined') {
      const currentVideo = videoRefs.current[activeIndex];
      const previousTime = videoTimes.current[activeIndex] || 0;

      if (currentVideo) {
        // Load video if not already loaded
        if (currentVideo.readyState === 0) {
          currentVideo.load();
        }

        const playVideo = () => {
          currentVideo.currentTime = previousTime;
          currentVideo.play().catch((error) => {
            console.error('Autoplay failed:', error);
          });
        };

        // Wait for video to be ready
        if (currentVideo.readyState >= 2) {
          playVideo();
        } else {
          currentVideo.addEventListener('canplay', playVideo, { once: true });
        }
      }
    }
  };

  const storeVideoTimeOnSlideChange = (swiper) => {
    // Use realIndex for loop mode
    const previousIndex = swiper.slides[swiper.previousIndex]?.getAttribute('data-swiper-slide-index')
                          ?? swiper.previousIndex;
    if (typeof previousIndex !== 'undefined') {
      const previousVideo = videoRefs.current[previousIndex];
      if (previousVideo) {
        videoTimes.current[previousIndex] = previousVideo.currentTime;
        // Pause the previous video to save resources
        previousVideo.pause();
      }
    }
  };

  const pauseAllVideosExcept = (activeIndex) => {
    videoRefs.current.forEach((video, index) => {
      if (video && index !== activeIndex) {
        video.pause();
      }
    });
  };

  const accessibleIds = useMemo(() => {
    return myProducts.length > 0 ? new Set(myProducts.map(p => p._id)) : null;
  }, [myProducts]);

  const productsById = useMemo(() => {
    const map = new Map();
    pickedProducts.forEach((product) => map.set(String(product._id), product));
    return map;
  }, [pickedProducts]);

  // The block layout drives what renders and in which order. Until the settings
  // call answers (or if it answers with nothing), fall back to the historical
  // layout: hero on top, then every category with "featured" first.
  const blocks = useMemo(
    () => resolveSections(
      sections || buildDefaultSections(categories),
      { categories, productsById }
    ),
    [sections, categories, productsById]
  );

  const renderHero = (isFirst, section) => (
    <div
      className={`video-slider-container${isFirst ? ' hero-first' : ''}`}
      style={blockTextStyle(section)}
    >
        <Swiper
		  modules={[Pagination, Autoplay, EffectFade]}
		  spaceBetween={0}
		  slidesPerView={1}
		  loop={true}
		  effect="fade"
		  fadeEffect={{ crossFade: true }}
		  autoplay={{
			delay: 10000,
			disableOnInteraction: false,
			pauseOnMouseEnter: true,
		  }}
		  speed={800}
		  pagination={false}
		  onSwiper={(swiper) => {
			swiperRef.current = swiper;
		  }}
		  onSlideChange={(swiper) => {
			const idx = swiper.realIndex ?? swiper.activeIndex;
			setActiveSlideIndex(idx);
			storeVideoTimeOnSlideChange(swiper);
			pauseAllVideosExcept(idx);
			playVideoOnSlideChange(swiper);
		  }}
		>
		  {(mediaReady && mediaItems.length > 0) ? (
			mediaItems.map((media, index) => (
			  <SwiperSlide key={media.url}>
				<div className="video-background fade-in">
				  {media.type === 'video' ? (
					<video
					  ref={(el) => (videoRefs.current[index] = el)}
					  loop
					  muted
					  preload={index === 0 ? "metadata" : "none"}
					  playsInline
					  controls={false}
					  autoPlay={index === 0}
					  onCanPlayThrough={(e) => e.currentTarget.classList.add('loaded')}
					  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
					>
					  <source src={media.url} type="video/mp4" />
					  Your browser does not support the video tag.
					</video>
				  ) : media.url.startsWith('#') ? (
					<div
					  className="image-slide loaded"
					  style={{ backgroundColor: media.url }}
					/>
				  ) : (
					<img
					  src={media.url}
					  alt={`media-${index}`}
					  className="image-slide fade-in"
					  onLoad={(e) => e.currentTarget.classList.add('loaded')}
					/>
				  )}
				</div>
			  </SwiperSlide>
			))
		  ) : (
			<SwiperSlide key="placeholder">
			  <div className="video-background placeholder-slide">
				<div className="spinner" />
			  </div>
			</SwiperSlide>
		  )}
		</Swiper>

        {showText && (
          <div className="content fade-in">
            <h1 className="content-title">{dashboardSettings?.title}</h1>
            <p className="content-subtitle">{dashboardSettings?.subtitle}</p>
            {mediaItems[activeSlideIndex]?.videoLink && (
              <a
                href={mediaItems[activeSlideIndex].videoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-cta-button"
                onClick={(e) => e.stopPropagation()}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="white"
                  stroke="none"
                >
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
                <span>{mediaItems[activeSlideIndex].videoLinkText || 'Try it yourself'}</span>
              </a>
            )}
          </div>
        )}
      </div>
  );

  return (
    <div className="Home">
      {blocks.map((block, index) => {
        // Only the block right under the hero pulls up over its gradient.
        const overlapsHero = blocks[index - 1]?.type === 'hero';

        switch (block.type) {
          case 'hero':
            return <React.Fragment key={block.id}>{renderHero(index === 0, block)}</React.Fragment>;

          case 'categoryRow':
            return (
              <div
                key={block.id}
                className={`carousel-section${overlapsHero ? ' overlaps-hero' : ''}`}
                style={blockTextStyle(block)}
              >
                <ProductCarousel
                  category={block.category.name}
                  displayName={block.titleOverride || block.category.displayName}
                  products={block.category.products}
                  accessibleProductIds={accessibleIds}
                  isFeatured={!!block.featuredStyle}
                />
              </div>
            );

          case 'productRow':
            return (
              <div
                key={block.id}
                className={`carousel-section${overlapsHero ? ' overlaps-hero' : ''}`}
                style={blockTextStyle(block)}
              >
                <ProductCarousel
                  displayName={block.titleOverride}
                  staticProducts={block.products}
                  accessibleProductIds={accessibleIds}
                  isFeatured={!!block.featuredStyle}
                />
              </div>
            );

          case 'banner':
            return <BannerBlock key={block.id} section={block} />;

          case 'richText':
            return <RichTextBlock key={block.id} section={block} />;

          case 'spacer':
            return <SpacerBlock key={block.id} section={block} />;

          default:
            return null;
        }
      })}
    </div>
  );
}

export default Home;
